import { useCallback, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Hammer, Search, Settings } from 'lucide-react';
import { api, LitSquadInventoryEntry } from '@/lib/api';
import { useAuth } from '@/auth/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

import { FavoriteButton } from '../../../components/FavoriteButton';
import { DeleteItemDialog } from '../components/DeleteItemDialog';
import { InventoryGrid } from '../components/InventoryGrid';
import { TransferHistoryList } from '../components/TransferHistoryList';
import { TransferItemDialog } from '../components/TransferItemDialog';
import { useDebouncedSearch } from '../hooks/useDebouncedSearch';
import { useLitSquadInventory } from '../hooks/useLitSquadInventory';
import { useLitSquadTransferHistory } from '../hooks/useLitSquadTransferHistory';

export function InventoryPage() {
  const { user } = useAuth();
  const isAdmin = useMemo(
    () => user?.roles?.some((r) => r === 'admin' || r === 'litcraft_admin') ?? false,
    [user?.roles],
  );

  const { search, debouncedSearch, handleSearchChange } = useDebouncedSearch();
  const [sortBy, setSortBy] = useState('name');

  const { items, total, loading, error, refresh, loadMore, hasMore } = useLitSquadInventory({
    search: debouncedSearch || undefined,
    sortBy,
  });

  const [transferItem, setTransferItem] = useState<LitSquadInventoryEntry | null>(null);
  const [deleteItem, setDeleteItem] = useState<LitSquadInventoryEntry | null>(null);
  const navigate = useNavigate();

  const handleTransfer = useCallback(
    async (toUsername: string) => {
      if (!transferItem?.itemId) return;
      await api.transferLitSquadItem({ toUsername, itemId: transferItem.itemId });
      refresh();
    },
    [transferItem, refresh],
  );

  const handleDelete = useCallback(async () => {
    if (!deleteItem?.itemId) return;
    await api.burnLitSquadItem(deleteItem.itemId);
    refresh();
  }, [deleteItem, refresh]);

  const handleItemClick = useCallback(
    (item: LitSquadInventoryEntry) => {
      if (item.itemId) navigate(`/a/arche_litsquad/item/${item.itemId}`);
    },
    [navigate],
  );

  return (
    <div className="max-w-6xl mx-auto px-4">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-bold">My LitSquad</h1>
            <FavoriteButton archeId="arche_litsquad" />
          </div>
          <p className="text-muted-foreground text-sm">{total} NFTs</p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/a/arche_litsquad/craft">
            <Button size="sm">
              <Hammer className="h-4 w-4 mr-2" />
              Craft
            </Button>
          </Link>
          {isAdmin && (
            <Link to="/a/arche_litsquad/admin">
              <Button variant="outline" size="sm">
                <Settings className="h-4 w-4 mr-2" />
                Admin
              </Button>
            </Link>
          )}
        </div>
      </div>

      <Tabs defaultValue="inventory">
        <TabsList>
          <TabsTrigger value="inventory">Inventory</TabsTrigger>
          <TabsTrigger value="history">History</TabsTrigger>
        </TabsList>

        <TabsContent value="inventory" className="mt-4">
          <div className="flex gap-3 mb-6 flex-wrap">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => handleSearchChange(e.target.value)}
                placeholder="Search LitSquad NFTs..."
                className="pl-9"
              />
            </div>
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Sort" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="name">Name</SelectItem>
                <SelectItem value="acquired_at">Recently Acquired</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {error && (
            <div className="text-center py-4 text-destructive mb-4">
              <p>{error}</p>
              <Button variant="outline" size="sm" className="mt-2" onClick={refresh}>
                Retry
              </Button>
            </div>
          )}

          <InventoryGrid
            items={items}
            loading={loading}
            onTransfer={setTransferItem}
            onDelete={setDeleteItem}
            onClick={handleItemClick}
          />

          {hasMore && (
            <div className="text-center mt-6">
              <Button variant="outline" onClick={loadMore} disabled={loading}>
                {loading ? 'Loading...' : 'Load More'}
              </Button>
            </div>
          )}
        </TabsContent>

        <TabsContent value="history" className="mt-4">
          <TransferHistoryContent userId={user?.userId ?? ''} />
        </TabsContent>
      </Tabs>

      <TransferItemDialog
        item={transferItem}
        open={!!transferItem}
        onClose={() => setTransferItem(null)}
        onConfirm={handleTransfer}
      />

      <DeleteItemDialog
        item={deleteItem}
        open={!!deleteItem}
        onClose={() => setDeleteItem(null)}
        onConfirm={handleDelete}
      />
    </div>
  );
}

function TransferHistoryContent({ userId }: { userId: string }) {
  const { transfers, loading, error, loadMore, hasMore } = useLitSquadTransferHistory();

  return (
    <>
      {error && (
        <div className="text-center py-4 text-destructive mb-4">
          <p>{error}</p>
        </div>
      )}
      <TransferHistoryList
        transfers={transfers}
        currentUserId={userId}
        loading={loading}
        hasMore={hasMore}
        onLoadMore={loadMore}
      />
    </>
  );
}
